import React from "react";
import { Link } from "../components/L";
import useSEO from "../hooks/useSEO";
import { useLanguage } from "../i18n/LanguageContext";
import { TxHtml as H } from "../components/TxHtml";

const Section = ({ title, children }) => {
  const { tx } = useLanguage();
  return (
    <section className="mb-10">
      <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: "#003F72", fontFamily: "'Noto Sans', sans-serif" }}>
        {tx(title)}
      </h2>
      <div className="text-[#4A5568] leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
};

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5">
    {items.map((html) => <H key={html} as="li" html={html} />)}
  </ul>
);

const PrivacyPage = () => {
  const { tx } = useLanguage();
  useSEO({
    title: tx("Politika e Privatësisë - Albanian Aligners Academy"),
    description: tx("Politika e Privatësisë e Albanian Aligners Academy. Mësoni se si mbledhim, përdorim dhe mbrojmë të dhënat tuaja personale."),
    keywords: "politika e privatesise, privacy policy, mbrojtja e te dhenave, aligners academy",
    canonical: "https://aligners.academy/privatesia",
  });

  return (
    <div data-testid="privacy-page">
      <section className="relative py-10 md:py-14 px-6" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="privacy-hero">
        <div className="max-w-[1350px] mx-auto">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
            <Link to="/" className="hover:text-white text-white/70">{tx("Kryefaqe")}</Link>
            <span className="text-white/70">/</span>
            <span className="text-white">{tx("Politika e Privatësisë")}</span>
          </div>
          <h1 className="text-white" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: "42px", lineHeight: 1.2 }} data-testid="privacy-title">
            {tx("Politika e Privatësisë")}
          </h1>
          <p className="text-white/80 mt-3">{tx("Përditësuar më:")} 30.07.2026</p>
        </div>
      </section>

      {/* tx-catalog-start */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-[1350px] mx-auto">
          <H className="text-[#4A5568] leading-relaxed text-[15px] mb-10" html='Në <strong style="color:#003F72">Albanian Aligners Academy</strong>, privatësia juaj është e rëndësishme për ne. Kjo Politikë e Privatësisë shpjegon se si mbledhim, përdorim dhe mbrojmë të dhënat tuaja personale kur vizitoni faqen tonë të internetit ose regjistroheni në aktivitetet dhe shërbimet tona.' />

          <Section title="Çfarë të dhënash mbledhim?">
            <H html="Ne mund të mbledhim të dhëna si:" />
            <List items={[
              "Emër dhe mbiemër",
              "Adresë e-mail",
              "Numër telefoni",
              "Profesioni ose emri i klinikës (kur kërkohet)",
              "Informacion që na dërgoni përmes formularëve të kontaktit ose regjistrimit",
            ]} />
          </Section>

          <Section title="Si i përdorim të dhënat?">
            <H html="Të dhënat tuaja përdoren vetëm për:" />
            <List items={[
              "T'ju kontaktuar në lidhje me eventet, trajnimet ose shërbimet tona.",
              "Të përpunuar regjistrimet dhe rezervimet.",
              "T'ju dërguar informacione ose njoftime, vetëm nëse keni dhënë pëlqimin tuaj.",
              "Për të përmirësuar përvojën tuaj në faqen tonë.",
            ]} />
          </Section>

          <Section title="Ruajtja dhe siguria e të dhënave">
            <H html="Ne marrim masa të arsyeshme teknike dhe organizative për të mbrojtur të dhënat tuaja nga aksesi, përdorimi ose zbulimi i paautorizuar." />
          </Section>

          <Section title="Ndarja e të dhënave">
            <H html="Ne nuk i shesim dhe nuk i shpërndajmë të dhënat tuaja personale te palë të treta për qëllime tregtare. Të dhënat mund të ndahen vetëm në rastet e mëposhtme:" />
            <List items={[
              "<strong>POK Payments</strong> (procesuesi ynë i pagesave, i operuar nga Nebula Ltd) — kur kryeni një pagesë me kartë krediti/debiti, të dhënat e nevojshme për procesimin e transaksionit (emri, mbiemri, email-i, shuma dhe të dhënat e kartës) i transmetohen direkt POK-ut në përputhje me standardet PCI DSS. Ne <strong>nuk</strong> ruajmë asnjë të dhënë të kartës suaj. Të dhënat e transaksionit ruhen edhe në sistemet e POK Payments për arsye ligjore dhe kundër mashtrimit.",
              "<strong>Ofruesit e Email/SMS</strong> — përdorim shërbime SMTP dhe SMS për t'ju dërguar konfirmime, fatura dhe njoftime. Këto shërbime kanë akses vetëm në emrin, email-in dhe numrin e telefonit tuaj.",
              "<strong>Google Analytics &amp; Meta Pixel</strong> — për të kuptuar përdorimin e faqes në mënyrë anonime.",
              "Kur kërkohet me ligj nga autoritetet kompetente.",
            ]} />
          </Section>

          <Section title="Cookies">
            <H html="Faqja jonë mund të përdorë cookies për të përmirësuar funksionimin e saj dhe për të analizuar përdorimin e faqes. Ju mund t'i menaxhoni ose çaktivizoni cookies përmes cilësimeve të shfletuesit tuaj." />
          </Section>

          <Section title="Të drejtat tuaja">
            <H html="Ju keni të drejtë të kërkoni akses, korrigjim ose fshirje të të dhënave tuaja personale, si dhe të tërhiqni pëlqimin tuaj për komunikime marketingu në çdo kohë." />
          </Section>

          <Section title="Na kontaktoni">
            <H html="Për çdo pyetje në lidhje me këtë Politikë të Privatësisë ose për të ushtruar të drejtat tuaja, mund të na kontaktoni në:" />
            <p>
              Email:{" "}
              <a href="mailto:info@aligners.academy" className="text-[#1CA3B0] hover:underline font-semibold" data-testid="privacy-contact-email">
                info@aligners.academy
              </a>
            </p>
          </Section>

          <div className="mt-12 rounded-xl bg-slate-50 border border-slate-200 p-6">
            <H className="text-sm text-[#4A5568] leading-relaxed" html="Duke përdorur këtë faqe interneti, ju pranoni kushtet e kësaj Politike të Privatësisë." />
          </div>
        </div>
      </section>
      {/* tx-catalog-end */}
    </div>
  );
};

export default PrivacyPage;
